// src/components/team/TeamCommunicationHub.jsx
//
// Coach-side communication hub for a team's Overview page. Restores
// three features that lived on the unmounted Dashboard.jsx pre-Phase-1
// and went orphan when /dashboard was repurposed as the team picker:
//
//   1. Match Recap banner — link to the AI recap composer. The old
//      Dashboard had this as a passive prompt; here we surface it as
//      a small lime accent card so coaches notice it at a glance.
//
//   2. Quick Compose tiles — 4 one-tap message-type launchers
//      (Reminder, Game Recap, Schedule Change, Payment Due). Pre-
//      fills the AI composer with the right template via ?type=X.
//
//   3. Recent Broadcasts — last 3 AI messages sent for this team,
//      with type icon, label, body preview, and sent indicator.
//      Tap-through goes to the full /teams/:id/messages list.
//
// Coach-only — gated by the `canEdit` prop from the consumer. Parents
// don't see this; they have a different communication path (their
// inbox + the per-team announcement chat).

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection, query, orderBy, limit, onSnapshot,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { MESSAGE_TYPES } from '@/pages/messages-ai/messageTypes';
import {
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// Same priority order as the old Dashboard. Reminder + Recap +
// Schedule Change + Payment Due cover ~80% of mid-season broadcasts;
// less common types (Welcome, End-of-Season Thank You, etc.) are
// still reachable from the full composer at /messages/new.
const QUICK_TYPES = ['Reminder', 'Game Recap', 'Schedule Change', 'Payment Due'];

function formatRelative(ts) {
  if (!ts) return '';
  const ms = ts.toMillis ? ts.toMillis() : (ts.seconds ? ts.seconds * 1000 : 0);
  if (!ms) return '';
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function lookupType(id) {
  return MESSAGE_TYPES.find((m) => m.id === id) || { id, icon: '💬', label: id };
}

function SectionLabel({ children, className = '' }) {
  return (
    <p
      className={`text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] ${className}`}
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </p>
  );
}

export default function TeamCommunicationHub({ teamId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    const q = query(
      collection(db, 'teams', teamId, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(4),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRecentMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [teamId]);

  if (!teamId) return null;

  const quickTypes = MESSAGE_TYPES.filter((m) => QUICK_TYPES.includes(m.id));
  const top3 = recentMessages.slice(0, 3);

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] space-y-5">

      {/* Match Recap banner — high-conversion surface for the AI
          composer. Coaches who finished a game in the last day or two
          are likely to want to send a parent-friendly recap; this puts
          the action one tap away instead of buried in /messages/new. */}
      <Link
        to="/recap"
        className="flex items-center gap-3 rounded-xl border border-[#b5f03d]/40 bg-gradient-to-r from-[#b5f03d]/15 to-transparent px-4 py-3 hover:from-[#b5f03d]/25 transition-colors group"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/25">
          <SparklesIcon className="h-5 w-5 text-[#5a8a1f]" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[12px] font-bold uppercase tracking-[1.2px] text-[#3d6010]"
            style={{ fontFamily: HEAD_FONT }}
          >
            AI Match Recap
          </p>
          <p className="text-[11px] text-zinc-600 truncate mt-0.5">
            Draft a parent-friendly recap of your last game in seconds.
          </p>
        </div>
        <ArrowRightIcon className="h-4 w-4 text-zinc-400 group-hover:text-[#5a8a1f] shrink-0 transition-colors" />
      </Link>

      {/* Quick Compose — 2x2 grid of pre-typed broadcast launchers.
          Each tile passes ?type=X so the composer pre-fills the
          right AI prompt (Reminder template, Recap template, etc.). */}
      <div>
        <SectionLabel className="mb-2">Send something now</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          {quickTypes.map((mt) => (
            <button
              key={mt.id}
              type="button"
              onClick={() => navigate(`/teams/${teamId}/messages/new?type=${encodeURIComponent(mt.id)}`)}
              className="flex items-center gap-2.5 rounded-xl border border-zinc-200 bg-zinc-50 hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 px-3 py-2.5 text-left text-xs font-semibold text-[#0d1f14] transition-colors"
            >
              <span className="text-base shrink-0">{mt.icon}</span>
              <span className="leading-tight">{mt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent broadcasts — most-recent 3 messages from the team's
          AI message history. Provides the "is the assistant working?"
          confidence cue + a fast jump back into a draft they might
          want to review. */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <SectionLabel>Recent messages</SectionLabel>
          <Link
            to={`/teams/${teamId}/messages`}
            className="text-[11px] font-bold text-[#5a8a1f] hover:text-[#3d6010] uppercase tracking-wide transition-colors"
          >
            See all →
          </Link>
        </div>
        {loading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-14 rounded-xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        ) : top3.length > 0 ? (
          <div className="space-y-2">
            {top3.map((msg) => {
              const meta = lookupType(msg.messageType);
              return (
                <Link
                  key={msg.id}
                  to={`/teams/${teamId}/messages`}
                  className="block"
                >
                  <div className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5 transition-colors group">
                    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-[#b5f03d]/20 text-sm">
                      {meta.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-[#0d1f14] truncate">{meta.label}</span>
                        <span className="text-[10px] text-zinc-500 shrink-0">{formatRelative(msg.createdAt)}</span>
                      </div>
                      <p className="text-[11px] text-zinc-600 truncate mt-0.5">
                        {(msg.bodyEn || '').slice(0, 80)}
                        {(msg.bodyEn || '').length > 80 ? '…' : ''}
                      </p>
                    </div>
                    {/* Green check ONLY for fully-delivered broadcasts —
                        partial/failed sends get an amber warning instead
                        (2026-07-24 messages audit: this list showed a
                        success check on failed broadcasts while the
                        message history correctly downgraded them). */}
                    {msg.status === 'sent' && (
                      (msg.broadcastStatus === 'partial' || msg.broadcastStatus === 'failed') ? (
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                      ) : (
                        <CheckCircleIcon className="h-3.5 w-3.5 text-[#5a8a1f] shrink-0" />
                      )
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-zinc-300 p-5 text-center bg-zinc-50/50">
            <p className="text-xs text-zinc-600 mb-3">Haven't sent anything yet — want to say hi to the team?</p>
            <Link
              to={`/teams/${teamId}/messages/new`}
              className="inline-flex items-center gap-1.5 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3 py-1.5 text-xs font-bold uppercase transition-colors"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <SparklesIcon className="h-3.5 w-3.5" />
              Send your first message
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
